import { TranslocoPipe } from '@jsverse/transloco';
import { Component } from '@angular/core';
import { RevealDirective } from '../../../shared/animations/reveal.directive';
import { ChartViewComponent } from '../../stats/charts/chart-view.component';
import { DEMO_MOMENTUM, DEMO_SERVE_ARSENAL } from '../domain/landing-demo-data';

@Component({
  selector: 'app-landing-feature-rows',
  standalone: true,
  imports: [TranslocoPipe, RevealDirective, ChartViewComponent],
  template: `
    <section class="feature-rows" aria-labelledby="feature-rows-title">
      <div class="section-heading" appReveal>
        <span>{{ 'landing.afterMatchDashboard' | transloco }}</span>
        <h2 id="feature-rows-title">{{ 'landing.analysisThatFeelsReadyBeforeYouLeaveTheCourt' | transloco }}</h2>
      </div>

      <article class="feature-row feature-row--flipped" appReveal>
        <div class="feature-copy">
          <span>{{ 'landing.liveMatchTimeline' | transloco }}</span>
          <h3>{{ 'landing.scoreMomentumPointByPoint' | transloco }}</h3>
          <p>{{ 'landing.seeRunsBuildPressureFlipAndTheMomentEach' | transloco }}</p>
          <strong>{{ 'landing.copy7SwingOverFourConsecutivePoints' | transloco }}</strong>
        </div>
        <div class="feature-visual">
          @defer (on viewport) {
            <app-chart-view [chart]="momentum" />
          } @placeholder {
            <div class="chart-skeleton" aria-hidden="true"></div>
          }
        </div>
      </article>

      <article class="feature-row" appReveal>
        <div class="feature-copy">
          <span>{{ 'landing.serveAnalysis' | transloco }}</span>
          <h3>{{ 'landing.yourServeArsenalMapped' | transloco }}</h3>
          <p>{{ 'landing.sixServeSignalsShowYourWeaponsAndYourNext' | transloco }}</p>
          <strong>{{ serveArsenal.sampleSize }} serves · same Match Review sample</strong>
        </div>
        <div class="feature-visual">
          @defer (on viewport) {
            <app-chart-view [chart]="serveArsenal" />
          } @placeholder {
            <div class="chart-skeleton" aria-hidden="true"></div>
          }
        </div>
      </article>
    </section>
  `,
  styles: [`
    .feature-rows {
      display: grid;
      gap: clamp(2rem, 5vw, 3.5rem);
      margin-inline: auto;
      width: min(100%, 74rem);
    }

    .section-heading {
      display: grid;
      gap: var(--space-2);
      max-width: 44rem;
    }

    .section-heading span,
    .feature-copy span {
      color: var(--color-primary);
      font-size: var(--font-size-label);
      font-weight: 900;
      text-transform: uppercase;
    }

    h2, h3, p { margin: 0; }

    h2 {
      font-size: clamp(1.75rem, 4vw, 3.2rem);
      letter-spacing: 0;
      line-height: 1.05;
    }

    .feature-row {
      align-items: center;
      background: var(--color-surface);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      display: grid;
      gap: var(--space-6);
      grid-template-columns: minmax(16rem, 0.75fr) minmax(0, 1fr);
      padding: clamp(1rem, 3vw, 2rem);
    }

    .feature-row--flipped .feature-copy { order: 2; }
    .feature-row--flipped .feature-visual { order: 1; }

    .feature-copy {
      display: grid;
      gap: var(--space-3);
    }

    h3 {
      font-size: clamp(1.5rem, 3vw, 2.3rem);
      letter-spacing: 0;
      line-height: 1.08;
    }

    .feature-copy p { color: var(--color-text-secondary); line-height: 1.7; }
    .feature-copy strong { font-size: 1.05rem; font-weight: 900; }

    .feature-visual {
      background: var(--color-surface-high);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-card);
      min-width: 0;
      padding: var(--space-3) var(--space-2) var(--space-2);
    }

    .chart-skeleton {
      background:
        radial-gradient(circle at 30% 40%, color-mix(in srgb, var(--color-primary) 7%, transparent), transparent 60%),
        var(--color-surface-high);
      border-radius: var(--radius-lg);
      min-height: 24rem;
    }

    @media (max-width: 900px) {
      .feature-row { grid-template-columns: 1fr; }

      .feature-row--flipped .feature-copy { order: 1; }
      .feature-row--flipped .feature-visual { order: 2; }
    }
  `],
})
export class LandingFeatureRowsComponent {
  readonly momentum = DEMO_MOMENTUM;
  readonly serveArsenal = DEMO_SERVE_ARSENAL;
}
